import { useNavigate } from "react-router-dom";
import { IconFileCheck, IconUser } from "@tabler/icons-react";
import { Chip } from "../../../../components/common/chip";
import { Text } from "../../../../components/common/typography";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { EmptyState } from "../../../../components/common/empty-state";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { UserDocumentsDto } from "../../../../api";
import { useAdminDashboardDocuments } from "../../hooks/useAdminDashboardDocuments";
import { DashboardSectionCard } from "./dashboard-section-card";

type DocItem = Pick<UserDocumentsDto, "userId" | "userName" | "userEmail">;

function DocumentReviewItem({ row, onClick }: { row: DocItem; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border border-borderDefault bg-bgWhite px-4 py-3 text-left transition-colors hover:border-borderDark hover:bg-bgLight"
    >
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-bgLight text-textMuted">
        <IconUser size={20} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <Text variant="bodySmBold" tone="dark">{row.userName}</Text>
        <Text variant="bodyXs" tone="muted">{row.userEmail ?? "—"}</Text>
      </div>
      <Chip variant="inReview">Pending</Chip>
    </button>
  );
}

export function PendingDocumentReviewsCard() {
  const navigate = useNavigate();
  const { data, isLoading } = useAdminDashboardDocuments();
  const items: DocItem[] = ((data?.data as DocItem[] | undefined) ?? []).slice(0, 3);

  return (
    <DashboardSectionCard title="Pending Document Reviews" viewAllRoute={AppRoutes.ADMIN_DOCUMENTS}>
      {isLoading ? (
        <ShimmerLoader message="Loading document reviews…" lines={3} />
      ) : items.length === 0 ? (
        <EmptyState
          size="sm"
          icon={IconFileCheck}
          title="No documents waiting"
          description="All submitted documents have been reviewed."
        />
      ) : (
        items.map((row) => (
          <DocumentReviewItem
            key={row.userId}
            row={row}
            onClick={() =>
              navigate(AppRoutes.ADMIN_DOCUMENTS_USER.replace(":userId", row.userId))
            }
          />
        ))
      )}
    </DashboardSectionCard>
  );
}
